import clsx from 'clsx';
import {
  BarChart3,
  Briefcase,
  FileText,
  Gauge,
  KeyRound,
  Languages,
  LogOut,
  Medal,
  Menu,
  Moon,
  Settings2,
  Sparkles,
  Sun,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { VigLogo } from './Logo';
import { PeriodPicker } from './PeriodPicker';

const NAV: Array<{ group: TranslationKey; items: Array<{ to: string; label: TranslationKey; icon: typeof Gauge }> }> = [
  { group: 'nav.groupCompany', items: [{ to: '/', label: 'nav.overview', icon: Gauge }] },
  {
    group: 'nav.groupPeople',
    items: [
      { to: '/employees', label: 'nav.employees', icon: Users },
      { to: '/points', label: 'nav.points', icon: Medal },
      { to: '/work', label: 'nav.work', icon: BarChart3 },
      { to: '/projects', label: 'nav.projects', icon: Briefcase },
    ],
  },
  { group: 'nav.groupMoney', items: [{ to: '/finance', label: 'nav.finance', icon: Wallet }] },
  {
    group: 'nav.groupBriefings',
    items: [
      { to: '/insights', label: 'nav.insights', icon: Sparkles },
      { to: '/reports', label: 'nav.reports', icon: FileText },
    ],
  },
  {
    group: 'nav.groupAdmin',
    items: [
      { to: '/access', label: 'nav.access', icon: KeyRound },
      { to: '/settings', label: 'nav.settings', icon: Settings2 },
    ],
  },
];

const THEME_KEY = 'viganium.dashboard.theme';

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark';
    } catch {
      return 'dark';
    }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Kept for this visit only.
    }
  }, [theme]);
  return [theme, setTheme] as const;
}

/**
 * The frame: the sections on the start side (a drawer on a phone), the period and the account above the
 * page. Every link keeps the period, so moving between pages reads the same one.
 */
export function Layout({ children }: { children: ReactNode }) {
  const { t, locale, setLocale, localized } = useI18n();
  const { user, signOut } = useAuth();
  const { query } = usePeriod();
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  const sidebar = (
    <nav aria-label={t('nav.menu')} className="flex h-full flex-col gap-5 overflow-y-auto px-3 py-5">
      <div className="flex items-center gap-3 px-2">
        <VigLogo className="h-7 w-auto text-neutral-50" aria-hidden />
        <span className="sr-only">{t('app.name')}</span>
      </div>
      <p className="px-2 text-caption uppercase tracking-wide text-neutral-400">{t('app.short')}</p>
      {NAV.map((g) => (
        <div key={g.group}>
          <p className="mb-1 px-2 text-caption uppercase tracking-wide text-neutral-500">{t(g.group)}</p>
          <ul className="space-y-0.5">
            {g.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={{ pathname: item.to, search: query ? `?${query}` : '' }}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-body-sm transition-colors duration-fast',
                      isActive ? 'bg-white/10 text-neutral-50' : 'text-[var(--sidebar-text)] hover:bg-white/5 hover:text-neutral-50',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                  {t(item.label)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-full">
      <aside data-theme="dark" className="hidden w-64 shrink-0 border-e border-ink-line bg-[var(--sidebar-bg)] lg:block">
        <div className="sticky top-0 h-screen">{sidebar}</div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <button className="absolute inset-0 bg-black/50" aria-label={t('nav.closeMenu')} onClick={() => setOpen(false)} />
          <aside data-theme="dark" className="absolute inset-y-0 start-0 w-72 bg-[var(--sidebar-bg)]">
            <button className="absolute end-3 top-3 rounded p-1 text-neutral-300" onClick={() => setOpen(false)} aria-label={t('nav.closeMenu')}>
              <X className="h-5 w-5" aria-hidden />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b hairline bg-[var(--surface-page)]/90 backdrop-blur">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <button className="rounded-md p-1.5 text-[var(--text-secondary)] lg:hidden" onClick={() => setOpen(true)} aria-label={t('nav.menu')}>
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            <div className="min-w-0 flex-1">
              <PeriodPicker />
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-body-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
              >
                <Languages className="h-4 w-4" aria-hidden />
                {t('lang.switch')}
              </button>
              <button
                type="button"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="rounded-md p-2 text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
                aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
                title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
              </button>
              <span className="hidden max-w-[12rem] truncate px-2 text-body-sm text-[var(--text-secondary)] md:inline" title={user ? t('account.signedInAs', { name: localized(user) }) : undefined}>
                {user ? localized(user) : ''}
              </span>
              <button
                type="button"
                onClick={() => void signOut()}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-body-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
              >
                <LogOut className="h-4 w-4 flip-rtl" aria-hidden />
                <span className="hidden sm:inline">{t('account.signOut')}</span>
              </button>
            </div>
          </div>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6">{children}</main>
      </div>
    </div>
  );
}
